import { useEffect, useState } from "react";
import { FlashcardsTodayResponse, type FlashcardsTodayDeck } from "@edu/shared";
import { call } from "../../lib/api.ts";
import { askOnWake, nextAskMs } from "../../lib/poll.ts";
import { localDayStart } from "./review-queue.ts";

/**
 * Счётчик «Карточки» в кабинете ученика: сколько показать сегодня (10.2
 * `docs/flashcards-design.md`).
 *
 * ⚠️ **Стартовый чанк**: живёт в каркасе кабинета, как счётчик домашки. Отсюда
 * только корневой `@edu/shared`, сам тренажёр грузится `import()`, и только
 * когда у ученика есть хоть одна открытая колода (7.1): без колод чанк не нужен.
 *
 * **Опрос раз в десять минут** по правилам `lib/poll.ts`: пауза на скрытой
 * вкладке, отступ при обрыве не короче интервала, отсчёт от ответа, ни одного
 * запроса внахлёст. Возвращение вкладки решает `askOnWake`: прошло десять
 * минут, значит сразу, иначе остаток. Ручка та же `today`, что у экрана:
 * отдельная ручка счётчика без свёртки не знала бы, что пора повторять.
 *
 * ⚠️ Отказ проглатывается молча: число это справка, а не работа человека
 * (то же правило у счётчиков домашки и переписки).
 */

const INTERVAL_MS = 10 * 60_000;

/** `GET /v1/flashcards/today` с началом дня устройства. */
export function getFlashcardsToday(now: number = Date.now()): Promise<FlashcardsTodayResponse> {
  const dayStart = localDayStart(now);
  return call(`/v1/flashcards/today?dayStart=${String(dayStart)}`, {}, FlashcardsTodayResponse);
}

const stale = new Set<() => void>();

/**
 * Тренажёр после сессии говорит «счёт устарел», и опрос спрашивает сразу, а не
 * через десять минут. Событием, а не контекстом: тренажёр живёт в ленивом
 * чанке, а провайдер пришлось бы тянуть через весь каркас.
 */
export function flashcardsTodayStale(): void {
  for (const fn of stale) fn();
}

let preloaded = false;

/** Чанк тренажёра заранее (7.1): без сети холодного старта нет, а с открытой вкладкой есть. */
function preloadTrainer(): void {
  if (preloaded) return;
  preloaded = true;
  void import("./student-screens.ts").catch(() => {
    preloaded = false;
  });
}

export function useFlashcardsToday(enabled: boolean): { decks: FlashcardsTodayDeck[]; total: number } {
  const [decks, setDecks] = useState<FlashcardsTodayDeck[]>([]);

  useEffect(() => {
    if (!enabled) {
      setDecks([]);
      return;
    }
    let alive = true;
    let timer: number | null = null;
    let running = false;
    let fails = 0;
    let answeredAt = -Infinity;

    const clearTimer = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };
    const plan = (ms: number | null) => {
      clearTimer();
      if (alive && ms !== null) timer = window.setTimeout(() => void ask(), ms);
    };
    const ask = async () => {
      timer = null;
      if (!alive || running || document.visibilityState === "hidden") return;
      running = true;
      try {
        const res = await getFlashcardsToday();
        if (!alive) return;
        fails = 0;
        answeredAt = Date.now();
        setDecks(res.decks);
        // Колоды есть, значит экран тренажёра этому человеку нужен: без сети
        // холодного старта нет, а пролистать колоду можно и без карточек на сегодня
        if (res.decks.length > 0) preloadTrainer();
      } catch {
        fails += 1;
      } finally {
        running = false;
        if (alive) {
          plan(nextAskMs({ visible: document.visibilityState === "visible", fails, intervalMs: INTERVAL_MS }));
        }
      }
    };
    const wake = () => {
      if (document.visibilityState !== "visible") {
        clearTimer();
        return;
      }
      plan(askOnWake({ sinceAnswerMs: Date.now() - answeredAt, intervalMs: INTERVAL_MS }));
    };
    const now = () => plan(0);

    stale.add(now);
    document.addEventListener("visibilitychange", wake);
    void ask();
    return () => {
      alive = false;
      clearTimer();
      stale.delete(now);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [enabled]);

  const total = decks.reduce((n, d) => n + d.dueNow, 0);
  return { decks, total };
}

